import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CalendarDays, Clock, ExternalLink, Loader2, MessageSquare, Send, UserCheck, Users } from "lucide-react";
import PublicSiteLayout, { PageHero, PublicLinkButton, PUBLIC_LINKS } from "../components/PublicSiteLayout";
import { API, BACKEND_URL, api, buildViewerLoginPageUrl } from "../lib/api";
import { getViewerSession } from "../lib/viewerSession";
import ProfileBadgeStrip from "../components/ProfileBadgeStrip";

function formatEventDate(value) {
  if (!value) return "Date coming soon";
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value));
  } catch (err) {
    return value;
  }
}

function mediaUrl(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (raw.startsWith("http")) return raw;
  if (raw.startsWith("/api/")) return `${API.replace(/\/api$/, "")}${raw}`;
  if (raw.startsWith("runtime-media/")) return `${API}/runtime-media/${raw.replace("runtime-media/", "")}`;
  return raw;
}

function profileMediaUrl(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  if (raw.startsWith("/")) return `${BACKEND_URL}${raw}`;
  return raw;
}

export default function PublicEvents() {
  const { eventSlug } = useParams();
  const [session, setSession] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [commentDrafts, setCommentDrafts] = useState({});
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const { session: viewerSession } = await getViewerSession();
    setSession(viewerSession);
    try {
      if (eventSlug) {
        const { data } = await api.get(`/events/${encodeURIComponent(eventSlug)}`);
        setEvents([data]);
      } else {
        const { data } = await api.get("/events");
        setEvents(data.events || []);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Could not load events right now.");
    } finally {
      setLoading(false);
    }
  }, [eventSlug]);

  useEffect(() => {
    load();
  }, [load]);

  const replaceEvent = (updated) => {
    setEvents((current) => current.map((event) => (
      event.event_id === updated.event_id ? updated : event
    )));
  };

  const setRsvp = async (event, status) => {
    if (!session) {
      window.location.href = buildViewerLoginPageUrl("/events");
      return;
    }
    setBusy(`${event.event_id}-${status}`);
    setError("");
    setMessage("");
    try {
      const { data } = await api.post(`/events/${event.event_id}/rsvp`, { status });
      replaceEvent(data);
      setMessage(status === "none" ? "RSVP cleared." : "RSVP saved.");
    } catch (err) {
      setError(err.response?.data?.detail || "Could not save RSVP.");
    } finally {
      setBusy("");
    }
  };

  const addComment = async (event) => {
    if (!session) {
      window.location.href = buildViewerLoginPageUrl("/events");
      return;
    }
    const body = String(commentDrafts[event.event_id] || "").trim();
    if (!body) return;
    setBusy(`${event.event_id}-comment`);
    setError("");
    setMessage("");
    try {
      const { data } = await api.post(`/events/${event.event_id}/comments`, { body });
      replaceEvent(data);
      setCommentDrafts((current) => ({ ...current, [event.event_id]: "" }));
      setMessage("Comment added.");
    } catch (err) {
      setError(err.response?.data?.detail || "Could not add comment.");
    } finally {
      setBusy("");
    }
  };

  return (
    <PublicSiteLayout>
      <PageHero
        eyebrow="Community events"
        title="Events for The Ratterday Saints."
        body={eventSlug ? "Event details, RSVPs, and community comments." : "Upcoming community nights, game races, giveaways, special runs, and reasons to show up with the rest of the crew."}
        actions={(
          <>
            {session ? (
              <PublicLinkButton href="/profile" primary>
                <UserCheck className="w-4 h-4" /> View profile
              </PublicLinkButton>
            ) : (
              <a
                href={buildViewerLoginPageUrl("/events")}
                target="_top"
                rel="noopener"
                className="inline-flex items-center gap-2 px-5 py-3 rounded bg-[hsl(var(--currency-event))] text-white font-medium hover:brightness-110 transition"
              >
                <UserCheck className="w-4 h-4" /> Sign in to RSVP
              </a>
            )}
            <PublicLinkButton href={PUBLIC_LINKS.discord}>
              <MessageSquare className="w-4 h-4" /> Join Discord
            </PublicLinkButton>
          </>
        )}
      />

      {(error || message) && (
        <section className={`mt-8 rounded border px-4 py-3 text-sm ${
          error
            ? "border-red-500/40 bg-red-500/10 text-red-100"
            : "border-emerald-500/40 bg-emerald-500/10 text-emerald-100"
        }`}>
          {error || message}
        </section>
      )}

      <section className="mt-10">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4">
          <div>
            <div className="label-eyebrow text-[hsl(var(--currency-event))]">Schedule</div>
            <h2 className="text-2xl font-semibold tracking-tight text-[hsl(var(--foreground))] mt-1">{eventSlug ? "Event details" : "Soonest first"}</h2>
          </div>
          {!session && (
            <div className="text-sm text-[hsl(var(--muted-foreground))]">
              You can browse events while signed out. Twitch sign-in is required to RSVP or comment.
            </div>
          )}
        </div>

        {loading ? (
          <div className="surface bg-black/75 p-6 text-sm text-[hsl(var(--muted-foreground))] flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading events...
          </div>
        ) : events.length === 0 ? (
          <div className="surface bg-black/75 p-6 sm:p-7 text-sm text-[hsl(var(--muted-foreground))]">
            <CalendarDays className="w-8 h-8 text-[hsl(var(--currency-event))] mb-3" strokeWidth={1.5} />
            <h3 className="text-xl font-semibold tracking-tight text-[hsl(var(--foreground))]">No events posted yet.</h3>
            <p className="mt-2 max-w-2xl leading-relaxed">
              Check back later or join Discord for stream night planning and last-minute chaos.
            </p>
            <div className="mt-5">
              <PublicLinkButton href={PUBLIC_LINKS.discord}>
                <MessageSquare className="w-4 h-4" /> Join Discord
              </PublicLinkButton>
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            {events.map((event) => (
              <EventCard
                key={event.event_id}
                event={event}
                session={session}
                busy={busy}
                commentDraft={commentDrafts[event.event_id] || ""}
                onCommentChange={(value) => setCommentDrafts((current) => ({ ...current, [event.event_id]: value }))}
                onRsvp={setRsvp}
                onComment={addComment}
              />
            ))}
          </div>
        )}
      </section>
    </PublicSiteLayout>
  );
}

function EventCard({ event, session, busy, commentDraft, onCommentChange, onRsvp, onComment }) {
  const currentStatus = event.viewer_rsvp?.status || "";
  const commentBusy = busy === `${event.event_id}-comment`;
  return (
    <article className="public-event-card surface bg-black/75 overflow-hidden">
      {event.header_image_url && (
        <div className="aspect-[16/6] min-h-44 bg-black overflow-hidden">
          <img src={mediaUrl(event.header_image_url)} alt="" className="w-full h-full object-cover" />
        </div>
      )}
      <div className="p-5 sm:p-7">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="min-w-0">
            <div className="label-eyebrow text-[hsl(var(--currency-event))] flex items-center gap-2">
              <CalendarDays className="w-4 h-4" /> Community event
            </div>
            <h3 className="mt-2 text-3xl font-black uppercase tracking-normal leading-none text-[hsl(var(--foreground))] break-words">{event.title}</h3>
            <div className="mt-3 flex flex-wrap gap-3 text-sm text-[hsl(var(--muted-foreground))]">
              <span className="inline-flex items-center gap-2"><Clock className="w-4 h-4" /> {formatEventDate(event.starts_at)}</span>
              {event.ends_at && <span>Ends {formatEventDate(event.ends_at)}</span>}
            </div>
          </div>
          {event.share_url && (
            <a
              href={event.share_url}
              target="_blank"
              rel="noopener noreferrer"
              className="public-quiet-action"
            >
              Share event <ExternalLink className="w-4 h-4" />
            </a>
          )}
        </div>

        {event.description && (
          <p className="mt-5 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed whitespace-pre-wrap">{event.description}</p>
        )}

        <div className="mt-6 grid grid-cols-1 md:grid-cols-[1fr_1.2fr] gap-4">
          <div className="public-event-group">
            <div className="label-eyebrow text-[hsl(var(--currency-event))] flex items-center gap-2">
              <Users className="w-4 h-4" /> RSVP
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
              <InfoTile label="Going" value={event.rsvp_counts?.attending || 0} />
              <InfoTile label="Tentative" value={event.rsvp_counts?.tentative || 0} />
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <RsvpButton active={currentStatus === "attending"} busy={busy === `${event.event_id}-attending`} onClick={() => onRsvp(event, "attending")}>Going</RsvpButton>
              <RsvpButton active={currentStatus === "tentative"} busy={busy === `${event.event_id}-tentative`} onClick={() => onRsvp(event, "tentative")}>Tentative</RsvpButton>
              {currentStatus && (
                <RsvpButton busy={busy === `${event.event_id}-none`} onClick={() => onRsvp(event, "none")}>Clear</RsvpButton>
              )}
            </div>
            {event.viewer_rsvp && (
              <div className="mt-4 rounded border border-[hsl(var(--border))] bg-black/35 p-3">
                <ViewerIdentity viewer={event.viewer_rsvp} meta={event.viewer_rsvp.status === "tentative" ? "You are tentative" : "You are going"} />
              </div>
            )}
            {!session && <p className="mt-3 text-xs text-[hsl(var(--muted-foreground))]">Sign in with Twitch or Kick to RSVP.</p>}
          </div>

          <div className="public-event-group">
            <div className="label-eyebrow text-[hsl(var(--currency-event))] flex items-center gap-2">
              <MessageSquare className="w-4 h-4" /> Comments
            </div>
            <div className="mt-3 space-y-3 max-h-56 overflow-y-auto pr-1">
              {(event.comments || []).length === 0 ? (
                <div className="text-sm text-[hsl(var(--muted-foreground))]">No comments yet.</div>
              ) : (
                event.comments.map((comment) => (
                  <div key={comment.id} className="rounded bg-black/45 border border-[hsl(var(--border))] px-3 py-2">
                    <ViewerIdentity viewer={comment} meta={formatEventDate(comment.created_at)} />
                    <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))] whitespace-pre-wrap">{comment.body}</p>
                  </div>
                ))
              )}
            </div>
            <div className="mt-4 flex gap-2">
              <input
                value={commentDraft}
                onChange={(event) => onCommentChange(event.target.value)}
                disabled={!session || commentBusy}
                maxLength={500}
                aria-label="Event comment"
                placeholder={session ? "Add a comment..." : "Sign in to comment"}
                className="min-w-0 flex-1 h-10 px-3 rounded bg-black/45 border border-[hsl(var(--border))] text-sm text-[hsl(var(--foreground))] outline-none focus:border-[hsl(var(--currency-event))] disabled:opacity-60"
              />
              <button
                type="button"
                onClick={() => onComment(event)}
                disabled={!session || commentBusy || !commentDraft.trim()}
                className="inline-flex items-center gap-2 px-3 py-2 rounded bg-[hsl(var(--currency-event))] text-white text-sm font-medium hover:brightness-110 disabled:bg-black/50 disabled:text-[hsl(var(--muted-foreground))] disabled:border disabled:border-[hsl(var(--border))]"
              >
                {commentBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Send
              </button>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}

function ViewerIdentity({ viewer, meta = "" }) {
  const name = viewer?.display_name || viewer?.login || "Viewer";
  const accent = viewer?.accent_color || "hsl(var(--currency-event))";
  const avatar = profileMediaUrl(viewer?.profile_image_url);
  const title = viewer?.active_title;
  const badges = Array.isArray(viewer?.featured_badges) ? viewer.featured_badges : [];
  return (
    <div className="flex items-center gap-3 min-w-0">
      {avatar ? (
        <img
          src={avatar}
          alt=""
          className="h-9 w-9 rounded object-cover border shrink-0"
          style={{ borderColor: accent }}
        />
      ) : (
        <span className="h-9 w-9 rounded bg-black/60 border inline-flex items-center justify-center text-xs font-black text-[hsl(var(--foreground))] shrink-0" style={{ borderColor: accent }}>
          {initialsForName(name)}
        </span>
      )}
      <div className="min-w-0">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-sm font-semibold truncate" style={{ color: accent }}>{name}</span>
          <ProfileBadgeStrip badges={badges} />
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-[hsl(var(--muted-foreground))]">
          {title?.name && <span>{title.name}</span>}
          {viewer?.pronouns && <span>{viewer.pronouns}</span>}
          {meta && <span>{meta}</span>}
        </div>
      </div>
    </div>
  );
}

function initialsForName(value) {
  const clean = String(value || "").replace(/[_-]+/g, " ").trim();
  if (!clean) return "?";
  const words = clean.split(/\s+/).filter(Boolean);
  if (words.length >= 2) return `${words[0][0]}${words[1][0]}`.toUpperCase();
  return clean.slice(0, 2).toUpperCase();
}

function InfoTile({ label = "", value } = {}) {
  return (
    <div className="rounded bg-white/[0.035] p-3">
      <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))]">{label}</div>
      <div className="mt-1 text-lg font-semibold text-[hsl(var(--foreground))]">{value}</div>
    </div>
  );
}

function RsvpButton({ active = false, busy = false, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      aria-pressed={active}
      className={`inline-flex items-center gap-2 px-3 py-2 rounded border text-sm transition ${
        active
          ? "border-[hsl(var(--currency-event))] bg-[hsl(var(--currency-event))]/20 text-white"
          : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-white hover:border-[hsl(var(--currency-event))]"
      }`}
    >
      {busy && <Loader2 className="w-4 h-4 animate-spin" />}
      {children}
    </button>
  );
}
